import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CalendarToday,
  FastForward,
  Restaurant,
  Schedule
} from '@mui/icons-material';
import dayjs from 'dayjs';

interface MealPlanSlot {
  weekStart: string;
  weekEnd?: string;  // Formatted date string from backend
  label: 'partial' | 'current' | 'next';
  startDate: any;
  endDate?: any;
  isPartial: boolean;
  daysCount: number;
  daysUntilStart?: number;
  description: string;
}

interface MealPlanOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (weekStart: string, weekEnd?: string) => void;
  slots: MealPlanSlot[];
  loading?: boolean;
}

const MealPlanOptionsDialog: React.FC<MealPlanOptionsDialogProps> = ({
  open,
  onClose,
  onSelect,
  slots,
  loading = false
}) => {
  const [selectedOption, setSelectedOption] = useState<string>('');

  const handleConfirm = () => {
    const selected = slots.find(s =>
      s.weekStart === selectedOption
    );

    if (selected) {
      if (selected.isPartial) {
        // For partial weeks, send both start and end dates
        // Use weekEnd from backend if available, otherwise fall back to endDate
        const endDateStr = selected.weekEnd || dayjs(selected.endDate).format('YYYY-MM-DD');
        onSelect(selected.weekStart, endDateStr);
      } else {
        // For complete weeks, just send start date
        onSelect(selected.weekStart);
      }
    }
  };

  // Auto-select if only one option
  React.useEffect(() => {
    if (slots.length === 1) {
      setSelectedOption(slots[0].weekStart);
    }
  }, [slots]);

  const formatDateRange = (slot: MealPlanSlot) => {
    const start = dayjs(slot.weekStart);
    // Use weekEnd from backend if available, otherwise fall back to endDate or default
    const end = slot.weekEnd
      ? dayjs(slot.weekEnd)
      : slot.endDate
      ? dayjs(slot.endDate)
      : start.add(6, 'day');

    if (start.month() === end.month()) {
      return `${start.format('MMM D')} - ${end.format('D, YYYY')}`;
    } else {
      return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
    }
  };

  const getOptionIcon = (slot: MealPlanSlot) => {
    if (slot.isPartial) return <FastForward />;
    if (slot.daysUntilStart === 0) return <CalendarToday />;
    return <Schedule />;
  };

  const getOptionColor = (slot: MealPlanSlot) => {
    if (slot.isPartial) return 'info';
    if (slot.daysUntilStart === 0) return 'success';
    return 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Restaurant />
          Choose Your Meal Plan Start Date
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : slots.length === 0 ? (
          <Alert severity="info">
            No meal plan slots are currently available. Please try again later or contact support.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select when you'd like your meal plan to begin:
            </Typography>

            <RadioGroup
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              sx={{ mt: 2 }}
            >
              {slots.map((slot) => (
                <Box
                  key={slot.weekStart}
                  sx={{
                    border: 1,
                    borderColor: selectedOption === slot.weekStart ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    backgroundColor: selectedOption === slot.weekStart ? 'action.hover' : 'transparent',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => setSelectedOption(slot.weekStart)}
                >
                  <FormControlLabel
                    value={slot.weekStart}
                    control={<Radio />}
                    label={
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {getOptionIcon(slot)}
                          <Typography variant="subtitle1" fontWeight="medium">
                            {slot.isPartial ? 'Start Tomorrow' :
                             slot.daysUntilStart === 0 ? 'Start Today' :
                             `Start ${dayjs(slot.weekStart).format('dddd')}`}
                          </Typography>
                          <Chip
                            label={`${slot.daysCount} days`}
                            size="small"
                            color={getOptionColor(slot)}
                            variant="outlined"
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                          {formatDateRange(slot)}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                          {slot.isPartial ?
                            '✨ Get started right away with a shorter first week' :
                            slot.daysUntilStart === 0 ?
                            '📅 Begin your meal planning journey today' :
                            '📆 Start fresh with a complete 7-day meal plan'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>

            {slots.some(s => s.isPartial) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Partial Week:</strong> Perfect for getting started quickly! Your shopping list
                  will only include ingredients for the days in your plan.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedOption || loading}
        >
          Generate Meal Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MealPlanOptionsDialog;